import { BlockedListObj, ApprovedListObj, DialogDisplayProps, DialogProductProps, Price } from "./types";
import PublicApiService from "../../services/PublicApiService";
import { IProduct } from "../types";
import { Nullable } from "@reedexpo/mercury-badge";
import { ProductInfoProps } from "../../components/createBadge/form/emailInListDialog/EmailInListDialog";
import { REGISTRATION_TYPE } from "../enums";

class ApprovedBlockedHelper {
  private _blockedEmails: BlockedListObj[] = [];
  private _approvedList: ApprovedListObj[] = [];

  get blockedEmails(): BlockedListObj[] {
    return this._blockedEmails;
  }

  get approvedList(): ApprovedListObj[] {
    return this._approvedList;
  }

  initializeList = async (eventGbsCode: string) => {
    const blockedEmailsResult = await PublicApiService.getBlockedEmails(eventGbsCode);
    const approvedListResult = await PublicApiService.getApprovedList(eventGbsCode);
    this._blockedEmails = blockedEmailsResult.data;
    this._approvedList = approvedListResult.data;
  };

  isNotSameProduct = (submittedProductCode: Nullable<string>, listProductCodes: Nullable<string[]>) =>
    listProductCodes && listProductCodes.length > 0 && submittedProductCode ? Boolean(listProductCodes.find((sku) => sku === submittedProductCode)) : false;

  getProductByPreapprovedSku = (products: IProduct[], listProductCodes: Nullable<string[]>) =>
    listProductCodes ? products.find(({ sku }) => listProductCodes.find((preApprovedSku) => preApprovedSku === sku)) : undefined;

  getApprovedListDialogProps = (sku: Nullable<string>, submittedEmail: string, products: IProduct[]) => {
    const productCodeListByEmail = this.findApprovedByEmail(submittedEmail);
    const isEmailBlocked = this.validateEmail(submittedEmail);
    const isNotSameProduct = this.isNotSameProduct(sku, productCodeListByEmail?.pC || null);
    const isNotInTheList = !productCodeListByEmail?.e && !isEmailBlocked;

    let registrationType;
    if (isEmailBlocked) {
      registrationType = REGISTRATION_TYPE.BLOCKED;
    } else if (isNotInTheList) {
      registrationType = REGISTRATION_TYPE.NONE;
    } else {
      registrationType = REGISTRATION_TYPE.APPROVED;
    }
    const listProduct = this.getProductByPreapprovedSku(products, productCodeListByEmail?.pC || null);

    return {
      isNotSameProduct: isNotSameProduct,
      registeredProduct: listProduct,
      registrationType
    };
  };

  getProductBasedProps = (product: Nullable<IProduct>, customerType: Nullable<string>): Nullable<DialogProductProps> => {
    let description: Nullable<string> = null;
    let markdown: Nullable<string> = null;
    let productName: Nullable<string> = null;
    let regularPrice: Nullable<Price> = null;
    if (product) {
      const {
        rx_markdown_description,
        rx_description,
        name,
        price_range: {
          maximum_price: { regular_price }
        }
      } = product;
      description = rx_description;
      markdown = rx_markdown_description || null;
      productName = name;
      regularPrice = regular_price;
    }
    if (regularPrice && customerType && productName && (markdown || description)) {
      return { regularPrice, productName, markdown, description, customerType };
    }
    return null;
  };

  createGreatNewsDialogComponentProps = ({
    productName,
    markdown,
    description,
    regularPrice: { currency, value },
    customerType
  }: DialogProductProps): ProductInfoProps => {
    const formatter = new Intl.NumberFormat("en-GB", {
      currency,
      style: "currency"
    });
    return {
      title: productName,
      isMarkdown: !!markdown,
      description: (markdown || description) as string,
      priceWithCurrency: formatter.format(value),
      customerType: customerType
    };
  };

  isApprovedRegistrationProps = (displayProps: DialogDisplayProps): displayProps is ProductInfoProps => {
    return !displayProps.hasOwnProperty("email");
  };

  findBlockedByEmail = (email: string) =>
    this.blockedEmails?.find(({ e }) => e.toLowerCase().trim() === email.toLowerCase().trim()) || this.blockedEmails.find(({ e }) => email.toLowerCase().trim().endsWith(`@${e.toLowerCase().trim()}`));


  findApprovedByEmail = (email: string) =>
    this.approvedList?.find(({ e }) => e.toLowerCase().trim() === email.toLowerCase().trim()) || this.approvedList.find(({ e }) => email.toLowerCase().trim().endsWith(`@${e.toLowerCase().trim()}`));

  validateEmail = (email: string) => {
    const blockedEmailsEntry = this.findBlockedByEmail(email);
    const approvedListEntry = this.findApprovedByEmail(email);
    if (blockedEmailsEntry?.e && approvedListEntry?.e) {
      return !Boolean(blockedEmailsEntry?.e.includes('@') && !approvedListEntry?.e.includes('@'));
    }
    return !Boolean(blockedEmailsEntry?.e && !approvedListEntry);
  }

  shouldMarkProductAsPreapproved = (sku: string, submittedEmail: string) => {
    const preapprovedProducts = this.findApprovedByEmail(submittedEmail)?.pC;
    return preapprovedProducts ? Boolean(preapprovedProducts.find((productCode) => productCode === sku)) : false;
  }
}

export default new ApprovedBlockedHelper();
